import { css } from '@emotion/react';
import { FormConfig, SelectOption, TextArea } from '@lbcamden/lib-react';
import { z } from 'zod';

import { newNoteTypeSchema, noteTextSchema } from '~/common/schemas/notes';
import { Person } from '~/common/types/people';

export type NewNoteFormValues = z.infer<typeof newNoteFormSchema>;

export type NoteAssociation = z.infer<typeof noteAssociationSchema>;

const noteAssociationSchema = z.object({ type: newNoteTypeSchema, entityRef: z.string() });

export const newNoteFormSchema = z.object({
  attachTo: noteAssociationSchema,
  caution: z.boolean(),
  subtype: z.string().optional(),
  note: noteTextSchema,
});

export const newNoteFormConfig = (
  noteAssociationOptions: SelectOption<NoteAssociation>[],
  subtypeOptions: SelectOption[],
  hasSelectedType: boolean,
  isCaution: boolean,
): FormConfig<NewNoteFormValues> => ({
  fieldConfigs: {
    attachTo: {
      label: 'Attach note to',
      selectOptions: noteAssociationOptions,
    },
    caution: {
      label: 'Should this note be saved with a “Caution” flag?',
      readOnly: !subtypeOptions.some(({ value }) => value === 'CAUTION'),
    },
    subtype: {
      label: 'Subtype',
      readOnly: !hasSelectedType || isCaution,
      selectOptions: subtypeOptions,
    },
    note: {
      label: 'Details',
      type: 'textarea',
      css: css`
        ${TextArea} {
          min-height: 5rem;
        }
      `,
    },
  },
});

export const createNoteAssociationOptions = (
  propertyRef: string,
  payReference: string,
  people: Person[],
): SelectOption<NoteAssociation>[] => {
  const tenancyId = people[0]?.tenancyId ?? -1;

  const personOptions = people.map<SelectOption<NoteAssociation>>(({ personId, fullName }) => ({
    id: `person_${personId}`,
    label: fullName,
    value: { type: 'PAR', entityRef: String(personId) },
  }));

  return [
    { id: 'property', label: 'Property', value: { type: 'PRO', entityRef: propertyRef } },
    { id: 'tenancy', label: 'Tenancy', value: { type: 'TCY', entityRef: String(tenancyId) } },
    { id: 'rent', label: 'Rent', value: { type: 'RAC', entityRef: payReference } },
    ...personOptions,
  ];
};
